import { useState, useRef, useCallback } from 'react';

/**
 * Hook to track generation timeout status.
 * After `timeoutMs` (default 60s), sets status to 'slow'.
 * Call `startTimer()` when generation begins, `clearTimer()` when it ends.
 * Call `setFailed()` or `setCancelled()` on error/cancel.
 */
const useGenerationTimeout = (timeoutMs = 60000) => {
  const [generationStatus, setGenerationStatus] = useState(null);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    setGenerationStatus(null);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setGenerationStatus('slow');
    }, timeoutMs);
  }, [timeoutMs]);

  const clearTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const setFailed = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setGenerationStatus('failed');
  }, []);

  const setCancelled = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setGenerationStatus('cancelled');
  }, []);

  const dismissNotification = useCallback(() => {
    setGenerationStatus(null);
  }, []);

  return {
    generationStatus,
    startTimer,
    clearTimer,
    setFailed,
    setCancelled,
    dismissNotification,
  };
};

export default useGenerationTimeout;
